import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'

export type ExchangeLevel = 'team' | 'feature' | 'user_story'

export interface Attachment {
  id: string
  file_name: string
  file_size: number
  mime_type: string
  storage_path: string
  created_at: string
}

export interface ExchangeMessage {
  id: string
  level: ExchangeLevel
  user_story_id: string | null
  feature_id: string | null
  team_id: string | null
  author_id: string
  content: string
  is_from_ebuilder: boolean
  created_at: string
  author?: {
    id: string
    full_name: string | null
    email: string
  }
  attachments?: Attachment[]
}

interface FetchMessagesParams {
  level: ExchangeLevel
  scopeId: string
}

interface SendMessageParams {
  level: ExchangeLevel
  scopeId: string
  content: string
  files?: File[]
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
const MAX_FILES = 5
const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

async function fetchMessages({ level, scopeId }: FetchMessagesParams): Promise<ExchangeMessage[]> {
  let query = supabase
    .from('exchange_messages')
    .select(`
      *,
      author:users (
        id,
        full_name,
        email
      ),
      attachments (
        id,
        file_name,
        file_size,
        mime_type,
        storage_path,
        created_at
      )
    `)
    .eq('level', level)

  // Add the appropriate filter based on level
  if (level === 'user_story') {
    query = query.eq('user_story_id', scopeId)
  } else if (level === 'feature') {
    query = query.eq('feature_id', scopeId)
  } else if (level === 'team') {
    query = query.eq('team_id', scopeId)
  }

  const { data, error } = await query.order('created_at', { ascending: true })

  if (error) {
    throw new Error(error.message)
  }

  return data as ExchangeMessage[]
}

async function sendMessage({ level, scopeId, content, files }: SendMessageParams): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser()

  if (!user) {
    throw new Error('Not authenticated')
  }

  // Validate files if provided
  if (files && files.length > 0) {
    if (files.length > MAX_FILES) {
      throw new Error(`Maximum ${MAX_FILES} files allowed`)
    }

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File "${file.name}" exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`)
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        throw new Error(`File type "${file.type}" is not allowed`)
      }
    }
  }

  // Build the insert object based on level
  // Explicitly set null for unused foreign keys to satisfy CHECK constraint
  const insertData: Record<string, unknown> = {
    level,
    author_id: user.id,
    content: content || '',
    is_from_ebuilder: false,
    team_id: null,
    feature_id: null,
    user_story_id: null,
  }

  if (level === 'user_story') {
    insertData.user_story_id = scopeId
  } else if (level === 'feature') {
    insertData.feature_id = scopeId
  } else if (level === 'team') {
    insertData.team_id = scopeId
  }

  // Create the message first
  const { data: messageData, error: messageError } = await supabase
    .from('exchange_messages')
    .insert(insertData)
    .select('id')
    .single()

  if (messageError) {
    throw new Error(messageError.message)
  }

  const messageId = messageData.id

  // Upload files and create attachment records
  if (files && files.length > 0) {
    for (const file of files) {
      // Generate unique file path
      const fileExt = file.name.split('.').pop()
      const fileName = `${messageId}/${crypto.randomUUID()}.${fileExt}`

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('message-attachments')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        // Continue with other files even if one fails
        continue
      }

      // Create attachment record
      const { error: attachmentError } = await supabase
        .from('attachments')
        .insert({
          message_id: messageId,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          storage_path: fileName,
          uploaded_by: user.id,
        })

      if (attachmentError) {
        console.error('Attachment record error:', attachmentError)
      }
    }
  }
}

// Generic hook for all exchange levels
export function useExchangeMessages(level: ExchangeLevel, scopeId: string | undefined) {
  return useQuery({
    queryKey: ['exchangeMessages', level, scopeId],
    queryFn: () => fetchMessages({ level, scopeId: scopeId! }),
    enabled: !!scopeId,
  })
}

export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: sendMessage,
    onSuccess: (_, variables) => {
      // Invalidate messages
      queryClient.invalidateQueries({
        queryKey: ['exchangeMessages', variables.level, variables.scopeId]
      })

      // If files were uploaded at feature level, invalidate attachments
      if (variables.files && variables.files.length > 0 && variables.level === 'feature') {
        queryClient.invalidateQueries({
          queryKey: ['featureAttachments', variables.scopeId]
        })
      }
    },
  })
}

// Legacy hook for backward compatibility (US level)
export function useExchangeMessagesUS(userStoryId: string | undefined) {
  return useExchangeMessages('user_story', userStoryId)
}

// Get signed URL for attachment download
export async function getAttachmentUrl(storagePath: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('message-attachments')
    .createSignedUrl(storagePath, 3600) // 1 hour expiry

  if (error) {
    console.error('Error getting signed URL:', error)
    return ''
  }

  return data.signedUrl
}

// Export constants for UI validation
export { MAX_FILE_SIZE, MAX_FILES, ALLOWED_TYPES }
